import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	memo
} from "react";
import {
	TileData,
	TileRenderState
} from "./RenderState/common";
import {
	convertDimensionToPx,
	convertPositionToPx,
	GridCellPixelValues,
	GridPositionInPx
} from "./GridCellPixelValuesTracker";
import classNames from "classnames";
import { GridDimension } from "client/Api/Workspace/common";
import { AppType } from "client/Api/Apps/common";
import { TimeZone } from "client/DateAndTime/timezones";
import { getReactColorAttributes } from "client_legacy/ndaw/appColor";
import { TileContentDependencies } from "./useTileContentDependencies";
import { TileContent } from "./TileContent";
import {
	DragCallbackArgs,
	useDraggable,
	UseDraggableOptions
} from "client/Hooks";
import { InteractionState } from "./Tiles/TileContext";
import { Coordinate } from "client/Misc/commonTypes";
import {
	ensureNotNil,
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { mergeRefs } from "client/Misc/reactUtils";
import { ContextMenu, useContextMenu } from "client/Components";
import {
	Action,
	ActionGroup,
	ActionKind
} from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import {
	doesAppSupportCustomDimensions,
	getAvailableDimensions
} from "client/Features/Workspace/appTypeHelper";
import { CustomDimensionMenu } from "./Tiles/CustomDimensionMenu";
import { createObjectComparer } from "client/Misc/objectComparer";
import styles from "./Tile.module.less";

export type TileCallbacks = {
	onEdit: (id: number) => void;
	onResize: (id: number, dimension: GridDimension) => void;
	onDelete: (id: number) => void;
	onDragStart: (tileId: number, args: DragCallbackArgs) => void;
	onDragMove: (tileId: number, args: DragCallbackArgs) => void;
	onDragStop: (tileId: number, args: DragCallbackArgs) => void;
};

export type TileProps = {
	renderState: TileRenderState;
	cellPixelValues: GridCellPixelValues;
	isTileGroupEditable: boolean;
	isInEditMode: boolean;
	userTimezone: TimeZone;
	tileContentDependencies: TileContentDependencies;
	tileCallbacks: TileCallbacks;
	tileGridDisplacementSinceTileDragStarted: GridPositionInPx | undefined;
};

const TileInternal = (props: TileProps): ReactElement => {
	const {
		renderState,
		cellPixelValues,
		isTileGroupEditable,
		isInEditMode,
		userTimezone,
		tileContentDependencies,
		tileCallbacks,
		tileGridDisplacementSinceTileDragStarted
	} = props;

	const {
		data: tile,
		dimension,
		position,
		interactionState
	} = renderState;

	const isEditable: boolean = isTileGroupEditable;
	// we have a special case when a user is a shared tile group owner but has no access to tile's app
	// or doesn't have the required module for it, in this case tile is editable as we would like to change
	// its size or remove it, but edit tile dialog is blocked
	const isEditAllowed: boolean = isEditable
		&& !tile.appIsNotAccessible
		&& tile.appIsLicensed;

	const [dragMovement, setDragMovement] = useState<Coordinate | null>(null);

	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const [contextMenuHandler, contextMenuProps] = useContextMenu(ref);

	const {
		onDragStart: dragStartManagementHandler,
		onDragMove: dragMoveManagementHandler,
		onDragStop: dragStopManagementHandler
	} = tileCallbacks;

	const dragStartHandler = useCallback(
		(args: DragCallbackArgs): void => {
			setDragMovement(args.movement);

			dragStartManagementHandler(
				tile.id,
				args
			);
		},
		[dragStartManagementHandler, tile.id]
	);

	const dragMoveHandler = useCallback(
		(args: DragCallbackArgs): void => {
			setDragMovement(args.movement);

			dragMoveManagementHandler(
				tile.id,
				args
			);
		},
		[dragMoveManagementHandler, tile.id]
	);

	const dragStopHandler = useCallback(
		(args: DragCallbackArgs): void => {
			setDragMovement(null);

			dragStopManagementHandler(
				tile.id,
				args
			);
		},
		[dragStopManagementHandler, tile.id]
	);

	const dragOptions = useMemo(
		(): UseDraggableOptions => ({
			onStart: dragStartHandler,
			onMove: dragMoveHandler,
			onStop: dragStopHandler,
			isEnabled: isTileGroupEditable && renderState.interactionState === InteractionState.Selectable
		}),
		[
			dragMoveHandler,
			dragStartHandler,
			dragStopHandler,
			isTileGroupEditable,
			renderState.interactionState
		]
	);

	const { ref: dragRef } = useDraggable(dragOptions);

	useEffect(
		(): EffectWithDestructorResult => {
			if (interactionState === InteractionState.Default) {
				return;
			}

			const cancellation = new AbortController();

			// NOTE: native event is used here because react events travel through portals
			// meaning that if tile renders a dialog all context menu events from the dialog
			// will also trigger this handler
			ensureNotNil(ref.current)
				.addEventListener(
					"contextmenu",
					(args) => {
						args.preventDefault();

						if (isEditable) {
							contextMenuHandler(args);
						}
					},
					{
						signal: cancellation.signal
					}
				);

			return (): void => cancellation.abort();
		},
		[
			interactionState,
			isEditable,
			contextMenuHandler
		]
	);

	const { onEdit } = tileCallbacks;

	const editHandler = useMemo(
		(): (() => void) | undefined => {
			if (!isEditAllowed) {
				return undefined;
			}

			return (): void => onEdit(tile.id);
		},
		[
			isEditAllowed,
			tile.id,
			onEdit
		]
	);

	const { colorClass, colorStyle } = getReactColorAttributes(tile.color);

	let positionInPx: GridPositionInPx = convertPositionToPx(position, cellPixelValues);

	if (dragMovement) {
		// It is very likely that after the drag update (mainly tile grid, and thereby tile group, dimensions having changed)
		// which has resulted in the tile grid occupying a different position in the dom. Since tiles are placed relative
		// to the tile grid, we need to patch the position of the tile being dragged.
		const tileGridDisplacement: GridPositionInPx = tileGridDisplacementSinceTileDragStarted ?? { left: 0, top: 0};

		positionInPx = {
			left: (positionInPx.left + dragMovement.x) - tileGridDisplacement.left,
			top: (positionInPx.top + dragMovement.y) - tileGridDisplacement.top
		};
	}

	return (
		<div
			{...setTrackingId("tile", `|${tile.id}`)}
			className={classNames(
				styles.root,
				colorClass,
				{
					[styles.root_editable]: isEditable,
					[styles.root_hidden]: !renderState.isVisible,
					[styles.root_dragging]: isNotNil(dragMovement),
					[styles.root_editMode]: isInEditMode
				}
			)}
			style={{
				...colorStyle,
				...positionInPx,
				...convertDimensionToPx(dimension.dimension, cellPixelValues)
			}}
			ref={mergeRefs(ref, dragRef)}
			// the "data-app-type" attribute is intended for external applications (from before trackingIds)
			data-app-type={tile.type}
			data-tile-size={`${tile.dimension.columnSpan}x${tile.dimension.rowSpan}`}
		>
			{renderState.isVisible && (
				<TileContent
					renderState={renderState}
					userTimezone={userTimezone}
					dependencies={tileContentDependencies}
					onEdit={editHandler}
					onOpenTileMenu={contextMenuHandler}
				/>
			)}

			{contextMenuProps && (
				<TileActionMenu
					{...contextMenuProps}
					tile={tile}
					isEditAllowed={isEditAllowed}
					tileCallbacks={tileCallbacks}
				/>
			)}
		</div>
	);
};
TileInternal.displayName = "TileInternal";

export const Tile = memo(
	TileInternal,
	createObjectComparer<TileProps>()
		.shallow("tileCallbacks")
		.equals([
			"cellPixelValues",
			"isTileGroupEditable",
			"isInEditMode",
			"renderState",
			"tileContentDependencies",
			"tileGridDisplacementSinceTileDragStarted",
			"userTimezone"
		])
		.getResult()
);
Tile.displayName = "Tile";

type TileActionMenuProps = {
	associatedElement: HTMLElement;
	position: Coordinate;
	onClose(): void;
	tile: TileData<AppType>;
	isEditAllowed: boolean;
	tileCallbacks: TileCallbacks;
};

const TileActionMenu = (props: TileActionMenuProps): ReactElement => {
	const {
		associatedElement,
		position,
		onClose,
		tile,
		isEditAllowed,
		tileCallbacks
	} = props;

	const [isCustomDimensionMenuOpen, setIsCustomDimensionMenuOpen] = useState<boolean>(false);

	const {
		onEdit,
		onDelete,
		onResize
	} = tileCallbacks;

	const actions = useMemo(
		(): readonly TileAction[] => getActions(
			tile.type,
			tile.dimension,
			{
				edit: () => onEdit(tile.id),
				delete: () => onDelete(tile.id),
				customDimension: () => setIsCustomDimensionMenuOpen(true),
				resize: (x) => onResize(tile.id, x)
			}
		),
		[
			tile.type,
			tile.dimension,
			tile.id,
			onEdit,
			onDelete,
			onResize
		]
	);

	const actionContext = useMemo(
		(): TileActionContext => ({
			type: tile.type,
			isEditAllowed
		}),
		[isEditAllowed, tile.type]
	);

	const setCustomDimensionHandler = useCallback(
		(dimension: GridDimension): void => {
			onResize(tile.id, dimension);
			onClose();
		},
		[
			onResize,
			tile.id,
			onClose
		]
	);

	if (isCustomDimensionMenuOpen) {
		return (
			<CustomDimensionMenu
				associatedElement={associatedElement}
				position={position}
				initialDimension={tile.dimension}
				trackingId={generateTrackingId("tile-custom-dimension-menu", `|${tile.id}`)}
				onCancel={onClose}
				onBack={() => setIsCustomDimensionMenuOpen(false)}
				onSet={setCustomDimensionHandler}
			/>
		);
	}

	return (
		<ContextMenu
			associatedElement={associatedElement}
			position={position}
			onClose={onClose}
			actions={actions}
			context={actionContext}
			trackingId={generateTrackingId("tile-context-menu", `|${tile.id}`)}
			title={tile.name}
		/>
	);
};
TileActionMenu.displayName = "TileActionMenu";

type TileActionContext = {
	type: AppType;
	isEditAllowed: boolean;
};

type TileAction = Action<TileActionContext>;

const resizeGroup: ActionGroup = {
	kind: "regular",
	name: "Resize",
	title: localize("_Resize")
};

const customDimensionGroup: ActionGroup = {
	kind: "regular",
	name: "custom-dimension",
	title: localize("_CustomSize")
};

type TileActionHandlers = {
	edit: () => void;
	delete: () => void;
	resize: (dimension: GridDimension) => void;
	customDimension: () => void;
};

function getActions(
	type: AppType,
	dimension: GridDimension,
	handlers: TileActionHandlers
): readonly TileAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "Delete",
			title: localize("_DeleteFromWorkspace"),
			icon: "trash-can",
			canExecute: () => true,
			execute: () => handlers.delete(),
		},
		{
			kind: ActionKind.Regular,
			name: "EditTile",
			title: localize("_EditTile"),
			icon: "pen",
			canExecute: (x) => x.isEditAllowed,
			execute: () => handlers.edit(),
		},
		...getResizeOptions(type, dimension)
			.map((x): TileAction => ({
				kind: ActionKind.SelectOption,
				name: x.name,
				title: x.title,
				group: resizeGroup,
				canExecute: () => true,
				execute: () => handlers.resize(x.dimension),
				isChecked: x.isChecked
			})),
		{
			kind: ActionKind.Regular,
			name: "custom- dimension",
			title: localize("_CustomSize"),
			icon: "expand",
			group: customDimensionGroup,
			canExecute: (x) => doesAppSupportCustomDimensions(x.type),
			execute: () => handlers.customDimension(),
			closeMenuOnClick: false
		}
	];
}

function getResizeOptions(
	type: AppType,
	tileDimension: GridDimension
): readonly TileResizeOption[] {
	const sortedAvailableDimensions: readonly GridDimension[] =
		sortBy(
			sortBy(
				getAvailableDimensions(type),
				(x) => x.rowSpan
			),
			(x) => x.columnSpan
		);

	const result: TileResizeOption[] = sortedAvailableDimensions
		.map(
			dimension => {
				const name: string = `Resize_${dimension.columnSpan}x${dimension.rowSpan}`;

				const title: string = `${dimension.columnSpan} x ${dimension.rowSpan}`;

				const isChecked: boolean = tileDimension.columnSpan === dimension.columnSpan &&
					tileDimension.rowSpan === dimension.rowSpan;

				return {
					dimension,
					name,
					title,
					isChecked
				};
			}
		);

	const customResizeOption: TileResizeOption = {
		name: `Resize_${tileDimension.columnSpan}x${tileDimension.rowSpan}`,
		title: `${tileDimension.columnSpan} x ${tileDimension.rowSpan} (${localize("_Custom")})`,
		isChecked: true,
		dimension: tileDimension
	};

	let wasTileDimensionHandled: boolean = false;

	for (let i = 0; i < result.length; i++) {
		const { dimension } = result[i];

		if (dimension.columnSpan === tileDimension.columnSpan
			&& dimension.rowSpan === tileDimension.rowSpan
		) {
			wasTileDimensionHandled = true;
			break;
		}

		const shouldInsertCustomHere: boolean = dimension.columnSpan > tileDimension.columnSpan
			|| (
				dimension.columnSpan === tileDimension.columnSpan
				&& dimension.rowSpan > tileDimension.rowSpan
			);

		if (shouldInsertCustomHere) {
			result.splice(
				i,
				0,
				customResizeOption
			);

			wasTileDimensionHandled = true;
			break;
		}
	}

	if (!wasTileDimensionHandled) {
		result.push(customResizeOption);
	}

	return result;
}

type TileResizeOption = {
	dimension: GridDimension;
	name: string;
	title: string;
	isChecked: boolean;
};